import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Public character meta (mirrors the public profile page so we can render
// proper colors / names without an extra round-trip)
const CHARACTER_META = {
  orb:     { name: 'ORB Ai Assistant', color: '#586D52' },
  mena:    { name: 'Mena ORB',         color: '#8B5CF6' },
  katie:   { name: 'Katie ORB',        color: '#EC4899' },
  luke:    { name: 'Luke ORB',         color: '#3B82F6' },
  dorian:  { name: 'Dorian ORB',       color: '#F59E0B' },
  octavio: { name: 'Octavio ORB',      color: '#10B981' },
};

export default function CharacterStoryPage() {
  const { characterId, storyId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const meta = CHARACTER_META[characterId] || CHARACTER_META.orb;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/character/${characterId}/stories/${storyId}`);
        if (alive) setStory(res.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || 'Could not load story');
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [api, characterId, storyId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" data-testid="character-story-page">
      <div className="p-4">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white/80 dark:bg-[#2a2a2a] flex items-center justify-center shadow-sm hover:shadow-md transition-all"
          data-testid="story-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-6 pb-32">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="text-center text-muted-foreground mt-12">{error}</p>
        ) : story ? (
          <article className="bg-white/90 dark:bg-[#1a1a1a]/90 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: meta.color }}>
              <BookOpen className="w-3.5 h-3.5" />
              <span>By {meta.name}</span>
            </div>
            <h1 className="text-2xl font-extrabold mb-4" style={{ color: meta.color }} data-testid="story-title">
              {story.title}
            </h1>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-gray-800 dark:text-gray-200" data-testid="story-body">
                {story.body}
              </p>
            </div>
          </article>
        ) : null}
      </div>
    </div>
  );
}
